import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DiffViewer } from '@/components/DiffViewer'
import type { PromptDiffResponse } from '@sdr-agend/admin-schemas'

/**
 * `/admin/prompts/:id/history/:version` — diff between the requested version
 * and the currently active version (Story 3.7 §AC #9).
 *
 * The toggle between unified and side-by-side views lives client-side; the
 * server only returns raw before/after bodies plus version metadata.
 */
export function PromptDiffPage(): React.ReactElement {
  const { id, version } = useParams<{ id: string; version: string }>()
  const [view, setView] = useState<'unified' | 'split'>('unified')

  const { data, isLoading, isError } = useQuery<PromptDiffResponse>({
    queryKey: ['admin-prompts', id, 'diff', version],
    queryFn: async () =>
      api<PromptDiffResponse>(`/admin/prompts/${id ?? ''}/history/${version ?? ''}`),
    enabled: id !== undefined && version !== undefined,
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label="Voltar">
            <Link to={`/admin/prompts/${id ?? ''}/history`}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Diff {data?.diff !== undefined && `v${data.diff.fromVersion} ↔ v${data.diff.toVersion}`}
            </h1>
            {data?.diff !== undefined && (
              <p className="text-sm text-muted-foreground">{data.diff.campaignName}</p>
            )}
          </div>
        </div>
        <div className="flex gap-1" role="group" aria-label="Modo de visualização">
          <Button
            type="button"
            size="sm"
            variant={view === 'unified' ? 'default' : 'outline'}
            onClick={() => setView('unified')}
            aria-pressed={view === 'unified'}
          >
            Unificado
          </Button>
          <Button
            type="button"
            size="sm"
            variant={view === 'split' ? 'default' : 'outline'}
            onClick={() => setView('split')}
            aria-pressed={view === 'split'}
          >
            Lado a lado
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-md border p-4 text-sm text-muted-foreground">Carregando diff…</div>
      )}
      {isError && (
        <div className="rounded-md border p-4 text-sm text-destructive">Erro ao carregar diff.</div>
      )}
      {data?.diff !== undefined && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary">v{data.diff.fromVersion}</Badge>
            <span>{data.diff.fromActive ? 'ativa' : 'arquivada'}</span>
            <span>vs</span>
            <Badge variant="success">v{data.diff.toVersion}</Badge>
            <span>{data.diff.toActive ? 'ativa' : 'arquivada'}</span>
          </div>
          <DiffViewer
            fromBody={data.diff.fromBody}
            toBody={data.diff.toBody}
            fromLabel={`v${data.diff.fromVersion}`}
            toLabel={`v${data.diff.toVersion} (atual)`}
            view={view}
          />
        </>
      )}
    </div>
  )
}

export default PromptDiffPage
